import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { PromoCodeDialogProps } from '../types';

export function PromoCodeDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
}: PromoCodeDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-4xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>{title}</DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="TALABA50"
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value: 'percent' | 'fixed') => onFormDataChange(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent</SelectItem>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                Value * {formData.type === 'percent' ? '(1-100%)' : '(UZS)'}
              </Label>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))}
                min={1}
                max={formData.type === 'percent' ? 100 : undefined}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Max Uses (leave empty for unlimited)</Label>
              <Input
                type="number"
                value={formData.max_uses || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, max_uses: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder="Unlimited"
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Valid From (optional)</Label>
              <Input
                type="datetime-local"
                value={formData.valid_from ? new Date(formData.valid_from).toISOString().slice(0, 16) : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const date = new Date(e.target.value);
                    if (isNaN(date.getTime())) {
                      toast.error('Invalid date format');
                      return;
                    }
                    onFormDataChange(prev => ({ ...prev, valid_from: date.toISOString() }));
                  } else {
                    onFormDataChange(prev => ({ ...prev, valid_from: null }));
                  }
                }}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Valid Until (optional)</Label>
              <Input
                type="datetime-local"
                value={formData.valid_until ? new Date(formData.valid_until).toISOString().slice(0, 16) : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const localDate = new Date(e.target.value + ':00');
                    onFormDataChange(prev => ({ ...prev, valid_until: localDate.toISOString() }));
                  } else {
                    onFormDataChange(prev => ({ ...prev, valid_until: null }));
                  }
                }}
                className={cn(isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200')}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => onFormDataChange(prev => ({ ...prev, is_active: checked }))}
            />
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}>
            Cancel
          </Button>
          <Button onClick={onSubmit} className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90">{submitLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
